import type { FlexProps } from '@chakra-ui/react';
import { AspectRatio, Box, Flex, Icon, Text } from '@chakra-ui/react';
import { features } from '@ui/components/Features';
import { Lottie } from '@ui/components/Lottie';
import useBoostMultLockedVno from '@ui/hooks/useBoostMultLockedVno';
import useBoostTokens from '@ui/hooks/useBoostTokens';
import { useFountainPoolInfos } from '@ui/hooks/useFountainPoolInfos';
import { isFeatureEnabled } from '@ui/hooks/useIsFeatureEnabled';
import { useReservoirPoolInfos } from '@ui/hooks/useReservoirPoolInfos';
import useVnoBasicInfo from '@ui/hooks/useVnoBasicInfo';
import { useTranslations } from '@ui/i18n';
import type { GardenType } from '@ui/utils/garden';
import { GardenRuler } from '@veno-app/icons';
import React from 'react';

import { useDepositDrawer } from '../Vno/ReservoirAndFountain';
import { GardenWaterBoostCard } from './GardenBoostCard/GardenWaterBoostCard';
import LockVnoSmallCard from './LockVnoSmallCard';

export type GardenViewGrowWaterProps = {
  gardenType: GardenType;
};

const cruiserStakingEnabled = isFeatureEnabled(features.NftStaking);

const RULER_HEIGHT_MOBILE = 300;
const RULER_HEIGHT_DESKTOP = 440;
const RULER_CURSOR_HEIGHT = 25;
const RULER_LABEL_HEIGHT = 21;

function GardenViewGrowWater() {
  const t = useTranslations();
  const { boostMultiplier } = useGardenViewGrowWater();
  const { pools: fountainPools } = useFountainPoolInfos();
  const { pools: reservoirPools } = useReservoirPoolInfos();
  const { onOpen: openDepositDrawer } = useDepositDrawer();

  return (
    <>
      <Box position="relative" minH="600px">
        <Box
          maxW={{ desktop: '500px' }}
          marginX="auto"
          px={{
            base: 4,
            desktop: 0,
          }}
        >
          <Text textStyle="h4" mb="16px">
            {t(
              'Lock your VNO in Reservoir or Fountain to increase your watering boost',
            )}
          </Text>
          <GardenWaterBoostCard isExpandable={cruiserStakingEnabled} />
          <Flex gap={4} mt="8px">
            <LockVnoSmallCard
              type="RESERVOIR"
              onClick={() => {
                if (reservoirPools) {
                  openDepositDrawer({
                    colorTheme: 'gauze',
                    vaultInfo: {
                      ...reservoirPools[reservoirPools.length - 1],
                      type: 'RESERVOIR',
                      // all unused
                      status: 'UNLOCKED',
                      allocation: '0',
                      lockedStakes: [],
                      unlockedStakes: [],
                      stakeCount: 0,
                    },
                  });
                }
              }}
            />
            <LockVnoSmallCard
              type="FOUNTAIN"
              onClick={() => {
                if (fountainPools) {
                  openDepositDrawer({
                    colorTheme: 'gauze',
                    vaultInfo: {
                      ...fountainPools[fountainPools.length - 1],
                      type: 'FOUNTAIN',
                      // all unused
                      status: 'UNLOCKED',
                      allocation: '0',
                      lockedStakes: [],
                      unlockedStakes: [],
                      stakeCount: 0,
                    },
                  });
                }
              }}
            />
          </Flex>
        </Box>
        <Box
          mt={3}
          w="full"
          position={{
            base: 'relative',
            desktop: 'absolute',
          }}
          top="0px"
          bottom="0px"
          left="0px"
          zIndex="-1"
          px={4}
          pb="80px"
        >
          <WaterRuler
            h={{
              base: `${RULER_HEIGHT_MOBILE}px`,
              desktop: `${RULER_HEIGHT_DESKTOP}px`,
            }}
            zIndex="1"
          />
          {boostMultiplier !== undefined && (
            <Box
              position="absolute"
              left={0}
              right={0}
              top={{
                base:
                  getWaterTopValue(boostMultiplier, RULER_HEIGHT_MOBILE) + 'px',
                desktop:
                  getWaterTopValue(boostMultiplier, RULER_HEIGHT_DESKTOP) +
                  'px',
              }}
              bottom="0"
              overflow="hidden"
              zIndex="0"
              // top offset = height * -5% = (width * 0.5334) * -5%
              mt="calc((100%  / 0.5334) * -0.05)"
            >
              <AspectRatio ratio={0.5334} w="100%">
                <Lottie path="/lottie/Veno_Garden_Wave.json" />
              </AspectRatio>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
}

const WaterRuler = (props: FlexProps) => {
  const { boostMultiplier } = useGardenViewGrowWater();

  return (
    <Flex flexDir="column" h="100%" position="relative" {...props}>
      <Icon
        as={GardenRuler}
        w="20px"
        h="100%"
        position="absolute"
        left={0}
        top={0}
        zIndex={-1}
        preserveAspectRatio="none"
        color="#2B5B52"
        _dark={{
          color: 'brand-blue',
        }}
      />
      <Flex ml="30px" position="relative" flexDir="column" h="100%">
        {boostMultiplier !== undefined && (
          <Flex
            position="absolute"
            top={`calc(${getWaterTopValue(boostMultiplier, 100)}% - ${
              RULER_CURSOR_HEIGHT / 2
            }px)`}
            left="-8px"
            alignItems="center"
          >
            <Box
              borderWidth="3px 9px"
              borderLeftWidth="0px"
              borderColor="transparent"
              borderRightColor="garden-highlight"
              w="0"
              h="0"
            />
            <Box
              textStyle="bodySmallBold"
              color="white"
              rounded="5px"
              bgColor="garden-highlight"
              px="6px"
              py="2px"
              _dark={{
                color: 'brand-primary',
              }}
            >
              {boostMultiplier?.toFixed(2)}x
            </Box>
          </Flex>
        )}
        {[1.75, 1.5, 1.25, 1.0].map((multiplier) => {
          return (
            <Text
              key={multiplier}
              hidden={boostMultiplier === multiplier}
              position="absolute"
              top={`calc(${getWaterTopValue(multiplier, 100)}% - ${
                RULER_LABEL_HEIGHT / 2
              }px)`}
              textStyle="bodySmallBold"
            >
              {multiplier.toFixed(2)}x
            </Text>
          );
        })}
      </Flex>
    </Flex>
  );
};

const getWaterTopValue = (
  boostMultiplier: number | undefined = 1,
  height: number,
) => {
  const min = 1;
  const max = 1.75;
  const ratio = (boostMultiplier - min) / (max - min);

  return height - ratio * height;
};

const useGardenViewGrowWater = () => {
  const { apr12month: apr12monthReservoir } = useVnoBasicInfo('RESERVOIR');
  const { apr12month: apr12monthFountain } = useVnoBasicInfo('FOUNTAIN');

  const { boostMultiplier } = useBoostMultLockedVno();
  const nextBoostMultiplier = boostMultiplier
    ? Math.min(1.75, boostMultiplier + 0.05)
    : undefined;

  const {
    isLoading: progressLoading,
    remainingIn96,
    progress,
    isMax,
  } = useBoostTokens();

  const boostPercent = progressLoading
    ? undefined
    : isMax
    ? 100
    : (progress ?? 0) * 100;

  return {
    apr12monthReservoir,
    apr12monthFountain,
    boostMultiplier,
    nextBoostMultiplier,
    boostPercent,
    remainingIn96,
  };
};

export default GardenViewGrowWater;
